import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { CustomEase } from "gsap/CustomEase";
import { SplitText } from "gsap/SplitText";

const sharedAnimations = () => {
  // Registering a plugin multiple times doesn't hurt anything
  gsap.registerPlugin(ScrollTrigger, CustomEase, SplitText);

  /*
    A phone hides its address bar on the first scroll down and shows it again on
    the way back up. Each of those changes the viewport height, and by default
    every one of them refreshes every ScrollTrigger on the page mid-gesture: a
    trigger that has not fired yet is recalculated as already behind the scroll
    position, so its timeline is set to its end state rather than played. The
    symptom is animations that work on a desktop and appear to be missing on a
    phone, and it repeats on every change of scroll direction.

    The flag only takes effect on touch-only devices, and even there it only
    swallows a resize that leaves the width alone and moves the height by less
    than a quarter of the viewport — an address bar is nowhere near that. An
    orientation change refreshes as it always did, because the width moves with
    it, and a desktop window is never affected at all.
  */
  if (!sharedAnimations.configured) {
    sharedAnimations.configured = true;
    ScrollTrigger.config({ ignoreMobileResize: true });
  }

  if (!sharedAnimations.defaultDuration) {
    sharedAnimations.defaultDuration = 0.4;
  }

  if (!sharedAnimations.generateTimeline) {
    sharedAnimations.generateTimeline = (section, config) => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: section.current,
          start: config?.start ? config.start : "top bottom-=100",
          markers: config?.markers ? config.markers : false,
        },
        onComplete: () => {
          tl.kill();
        },
      });

      return tl;
    };
  }

  if (!CustomEase.get("default")) {
    CustomEase.create("default", "0.25, 0.64, 0.72, 0.92");
  }

  // Rises out from behind a mask. Targets need a clipping ancestor.
  if (!gsap.effects.maskRiseIn) {
    gsap.registerEffect({
      name: "maskRiseIn",
      effect: (targets, config) =>
        gsap.fromTo(
          targets,
          { yPercent: 110 },
          {
            yPercent: 0,
            duration: config.duration,
            ease: "default",
            stagger: config.stagger,
          }
        ),
      defaults: { duration: 1.4, stagger: 0 },
      extendTimeline: true,
    });
  }

  // Draws a rule out from one end. `origin` picks which end it grows from.
  if (!gsap.effects.lineGrowIn) {
    gsap.registerEffect({
      name: "lineGrowIn",
      effect: (targets, config) =>
        gsap.fromTo(
          targets,
          { scaleX: 0, transformOrigin: config.origin },
          {
            scaleX: 1,
            transformOrigin: config.origin,
            duration: config.duration,
            ease: "default",
          }
        ),
      defaults: { duration: 2.4, origin: "left center" },
      extendTimeline: true,
    });
  }

  // Uncovers downwards. Swap to inset(100% 0 0 0) to reveal upwards instead.
  if (!gsap.effects.wipeUpIn) {
    gsap.registerEffect({
      name: "wipeUpIn",
      effect: (targets, config) =>
        gsap.fromTo(
          targets,
          { clipPath: "inset(0% 0% 100% 0%)" },
          {
            clipPath: "inset(0% 0% 0% 0%)",
            duration: config.duration,
            // Scrubbed callers pass "none"; an ease front-loads the reveal.
            ease: config.ease,
            stagger: config.stagger,
          }
        ),
      defaults: { duration: 1.6, stagger: 0.16, ease: "default" },
      extendTimeline: true,
    });
  }

  /*
    Rises a full line-height, so pair it with a mask. Travelling less than a
    whole line moved the text under 0.2px per frame, too fine for glyph
    rasterisation to render smoothly, and it read as jerky at a solid 60fps.
  */
  if (!gsap.effects.linesFadeUp) {
    gsap.registerEffect({
      name: "linesFadeUp",
      effect: (targets, config) =>
        gsap.fromTo(
          targets,
          {
            autoAlpha: 0,
            yPercent: 100,
            // force3D alone doesn't guarantee Chrome composites text; without
            // a layer every frame re-rasterises the glyphs. clearProps below
            // drops the hint once the tween is done.
            willChange: "transform, opacity",
          },
          {
            autoAlpha: 1,
            yPercent: 0,
            duration: config.duration,
            ease: "default",
            stagger: config.stagger,
            force3D: true,
            clearProps: "willChange",
          }
        ),
      defaults: { duration: 1.1, stagger: 0.14 },
      extendTimeline: true,
    });
  }

  if (!gsap.effects.contentSlideFadeIn) {
    gsap.registerEffect({
      name: "contentSlideFadeIn",
      effect: (targets, config) => {
        return gsap.fromTo(
          targets,
          {
            opacity: 0,
            translateY: 100,
          },
          {
            opacity: 1,
            translateY: 0,
            duration: sharedAnimations.defaultDuration,
            ease: "default",
            stagger: sharedAnimations.defaultDuration,
          }
        );
      },
      extendTimeline: true,
    });
  }
};

export default sharedAnimations;
